import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PremiumHero from '../components/PremiumHero';
import HiddenPageMessage from '../components/HiddenPageMessage';
import PreviewBanner from '../components/PreviewBanner';
import MediaGallerySection from '../components/MediaGallerySection';
import DocumentViewerSection from '../components/DocumentViewerSection';
import { DesignerSection, DESIGNER_SECTION_TYPES } from '../components/DesignerSections';
import { useVisibility } from '../hooks/useVisibility';
import { usePreviewMode, fetchWithPreview } from '../hooks/usePreviewMode';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Mail, Loader2, Users, Star, Lock, Handshake } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GolfOutingPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    organization: '',
    title: '',
    email: '',
    phone: '',
    participation_interest: '',
    team_name: '',
    sponsorship_interests: [],
    additional_notes: ''
  });
  const [errors, setErrors] = useState({});
  
  // Check page visibility
  const { isPageVisible, getPageHiddenMessage, loading: visibilityLoading } = useVisibility();
  const { preview } = usePreviewMode();
  const [isDraft, setIsDraft] = useState(false);
  const [extraSections, setExtraSections] = useState([]);

  useEffect(() => {
    let ignore = false;
    window.scrollTo(0, 0);
    (async () => {
      try {
        const [{ data, isDraft: draft }, extraRes] = await Promise.all([
          fetchWithPreview(`${API_URL}/api/golf/content`, { previewEnabled: preview }),
          fetchWithPreview(`${API_URL}/api/homepage/public?page=golf`, { previewEnabled: preview }),
        ]);
        if (ignore) return;
        setIsDraft(draft);
        setExtraSections(extraRes?.data?.sections || []);
        if (data) {
          setContent(data);
        }
      } catch (error) {
        console.error('Failed to fetch golf content:', error);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [preview]);

  const fetchContent = async () => {
    try {
      const { data, isDraft: draft } = await fetchWithPreview(`${API_URL}/api/golf/content`, { previewEnabled: preview });
      setIsDraft(draft);
      if (data) {
        setContent(data);
      }
    } catch (error) {
      console.error('Failed to fetch golf content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleRadioChange = (value) => {
    setFormData(prev => ({ ...prev, participation_interest: value }));
    if (errors.participation_interest) {
      setErrors(prev => ({ ...prev, participation_interest: null }));
    }
  };

  const handleCheckboxChange = (value, checked) => {
    setFormData(prev => ({
      ...prev,
      sponsorship_interests: checked
        ? [...prev.sponsorship_interests, value]
        : prev.sponsorship_interests.filter(v => v !== value)
    }));
    if (errors.sponsorship_interests) {
      setErrors(prev => ({ ...prev, sponsorship_interests: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.email.includes('@')) newErrors.email = 'Please enter a valid email';
    if (!formData.participation_interest) newErrors.participation_interest = 'Please select your interest level';
    if (formData.sponsorship_interests.length === 0) newErrors.sponsorship_interests = 'Please select at least one sponsorship option';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  // If page is hidden, show the hidden message (placed after all hooks)
  if (!visibilityLoading && !isPageVisible('golf')) {
    const message = getPageHiddenMessage('golf');
    return (
      <HiddenPageMessage
        header={message.header}
        subtitle={message.subtitle}
        body={message.body}
      />
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      // Save to database
      const response = await fetch(`${API_URL}/api/golf/inquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: formData.full_name,
          organization: formData.organization || null,
          title: formData.title || null,
          email: formData.email,
          phone: formData.phone || null,
          participation_interest: formData.participation_interest,
          team_name: formData.team_name || null,
          sponsorship_interests: formData.sponsorship_interests,
          additional_notes: formData.additional_notes || null
        })
      });

      if (response.ok) {
        // Show confirmation
        setIsSubmitted(true);
        toast.success('Your interest has been submitted successfully!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#00457f]" />
        </div>
      </div>
    );
  }

  const whyIcons = [Users, Star, Lock, Handshake];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="golf-outing-page">
      <PreviewBanner isDraft={isDraft} draftMeta={isDraft ? { updated_at: new Date().toISOString() } : null} pageLabel="Golf" />
      <Navigation />

      {/* Hero Section - Premium Design */}
      <PremiumHero
        title={content?.hero_title || '1st Annual WNC Golf Outing'}
        subtitle={content?.hero_subtitle || 'An exclusive golf experience for Congress participants and invited guests.'}
        overline={content?.hero_tagline || 'Interest & Sponsorship Inquiry'}
        testId="golf-hero-title"
        editScope={{ type: 'golf' }}
      />

      {/* Intro Text Block */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-6">
            <p className="text-amber-800 font-medium">
              {content?.intro_notice || 'This is the first annual event and details are currently being finalized.'}
            </p>
          </div>
          <p className="text-gray-700 leading-relaxed">
            {content?.intro_text || 'Location, day, time, format, tee gifts, and prizes are currently being discussed and will be announced soon. Early participants and sponsors are encouraged to express interest now and be part of shaping the inaugural outing.'}
          </p>
        </div>
      </section>

      {/* Why This Event Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-[#00457f] text-center mb-10">
            {content?.why_title || 'Why This Event'}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {(content?.why_points || [
              "Designed as a premium networking experience",
              "Limited participation",
              "Invitation-aligned atmosphere",
              "Built for meaningful interaction among leaders"
            ]).map((point, idx) => {
              const Icon = whyIcons[idx % whyIcons.length];
              return (
                <div 
                  key={idx} 
                  className="flex items-start gap-4 bg-white rounded-xl p-6 shadow-sm border border-gray-100"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#00457f]/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#00457f]" />
                  </div>
                  <p className="text-gray-700 font-medium">{point}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Aerial Golf Image Section */}
      <section className="relative h-64 md:h-80 overflow-hidden">
        <img
          src="https://customer-assets.emergentagent.com/job_8f2f50e5-b9bb-4da5-a7da-69fec80d19b9/artifacts/rcjuj79o_golf-tent-aerial-view-during-tournament_1024x1024.jpeg"
          alt="Aerial view of golf tournament"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-transparent to-white"></div>
      </section>

      {/* Interest Form Section */}
      <section className="py-16 bg-white" id="interest-form">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-[#00457f] text-center mb-10">
            {content?.form_title || 'Indicate Your Interest'}
          </h2>

          {isSubmitted ? (
            <Card className="border-2 border-green-200 shadow-lg">
              <CardContent className="p-10 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-green-700 mb-3">Thank You!</h3>
                <p className="text-gray-700 mb-6">
                  {content?.confirmation_message || 'Thank you for your interest. Details will be shared as soon as they are finalized.'}
                </p>
                <p className="text-gray-500 text-sm mb-6">
                  Please complete and send the email that opened in your email client.
                </p>
                <Button
                  onClick={() => setIsSubmitted(false)}
                  variant="outline"
                  className="border-[#00457f] text-[#00457f]"
                >
                  Submit Another Inquiry
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-2 border-gray-200 shadow-lg">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Contact Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Contact Information</h3>
                    
                    <div className="space-y-2">
                      <Label htmlFor="full_name" className="text-base font-medium text-gray-700">
                        Full Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="full_name"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        className={`h-12 ${errors.full_name ? 'border-red-500' : ''}`}
                        placeholder="John Smith"
                      />
                      {errors.full_name && <p className="text-red-500 text-sm">{errors.full_name}</p>}
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="organization" className="text-base font-medium text-gray-700">
                          Organization
                        </Label>
                        <Input
                          id="organization"
                          name="organization"
                          value={formData.organization}
                          onChange={handleInputChange}
                          className="h-12"
                          placeholder="Company Name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="title" className="text-base font-medium text-gray-700">
                          Title
                        </Label>
                        <Input
                          id="title"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          className="h-12"
                          placeholder="CEO, Director, etc."
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-base font-medium text-gray-700">
                          Email <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`h-12 ${errors.email ? 'border-red-500' : ''}`}
                          placeholder="john@example.com"
                        />
                        {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-base font-medium text-gray-700">
                          Phone <span className="text-gray-400">(optional)</span>
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="h-12"
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Participation Interest */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
                      Participation Interest <span className="text-red-500">*</span>
                    </h3>
                    <p className="text-gray-600 text-sm">Which best describes your interest?</p>
                    
                    <RadioGroup 
                      value={formData.participation_interest} 
                      onValueChange={handleRadioChange}
                      className="space-y-3"
                    >
                      <div className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:border-[#00457f]/50 transition-colors">
                        <RadioGroupItem value="help_joining_team" id="help_joining" />
                        <Label htmlFor="help_joining" className="flex-1 cursor-pointer font-normal">
                          I am interested and would like help joining a team
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:border-[#00457f]/50 transition-colors">
                        <RadioGroupItem value="have_four_person_team" id="have_team" />
                        <Label htmlFor="have_team" className="flex-1 cursor-pointer font-normal">
                          I am interested and have a four-person team
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:border-[#00457f]/50 transition-colors">
                        <RadioGroupItem value="want_more_details" id="want_details" />
                        <Label htmlFor="want_details" className="flex-1 cursor-pointer font-normal">
                          I am interested but want more details first
                        </Label>
                      </div>
                    </RadioGroup>
                    {errors.participation_interest && (
                      <p className="text-red-500 text-sm">{errors.participation_interest}</p>
                    )}

                    {/* Team Name - Conditional */}
                    {formData.participation_interest === 'have_four_person_team' && (
                      <div className="space-y-2 mt-4 pl-6 border-l-2 border-[#00457f]/30">
                        <Label htmlFor="team_name" className="text-base font-medium text-gray-700">
                          Team Name
                        </Label>
                        <Input
                          id="team_name"
                          name="team_name"
                          value={formData.team_name}
                          onChange={handleInputChange}
                          className="h-12"
                          placeholder="Enter your team name"
                        />
                      </div>
                    )}
                  </div>

                  {/* Sponsorship Interest */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">
                      Sponsorship Interest <span className="text-red-500">*</span>
                    </h3>
                    <p className="text-gray-600 text-sm">Select all that apply:</p>
                    
                    <div className="space-y-3">
                      {[
                        { value: 'entry_level', label: 'Entry Level Sponsor' },
                        { value: 'mid_level', label: 'Mid Level Sponsor' },
                        { value: 'title_sponsor', label: 'Title Sponsor' },
                        { value: 'not_at_this_time', label: 'Not at this time' }
                      ].map((option) => (
                        <div 
                          key={option.value}
                          className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:border-[#00457f]/50 transition-colors"
                        >
                          <Checkbox
                            id={option.value}
                            checked={formData.sponsorship_interests.includes(option.value)}
                            onCheckedChange={(checked) => handleCheckboxChange(option.value, checked)}
                          />
                          <Label htmlFor={option.value} className="flex-1 cursor-pointer font-normal">
                            {option.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                    {errors.sponsorship_interests && (
                      <p className="text-red-500 text-sm">{errors.sponsorship_interests}</p>
                    )}
                  </div>

                  {/* Additional Notes */}
                  <div className="space-y-2">
                    <Label htmlFor="additional_notes" className="text-base font-medium text-gray-700">
                      Anything you'd like us to know?
                    </Label>
                    <Textarea
                      id="additional_notes"
                      name="additional_notes"
                      value={formData.additional_notes}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Any additional information, questions, or special requests..."
                      className="resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#00457f] hover:bg-[#00365c] text-white font-bold text-lg py-6 rounded-lg transition-all duration-300 disabled:opacity-70"
                      data-testid="golf-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Mail className="w-5 h-5 mr-2" />
                          Submit Interest
                        </>
                      )}
                    </Button>
                    <p className="text-center text-gray-500 text-sm mt-3">
                      Your information will be saved and our team will contact you
                    </p>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {extraSections
        .filter(s => s.visible !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(s => {
          if (s.section_type === 'media_gallery') return <MediaGallerySection key={s.id} content={s.content || {}} sectionId={s.id} />;
          if (s.section_type === 'document_viewer') return <DocumentViewerSection key={s.id} content={s.content || {}} />;
          if (DESIGNER_SECTION_TYPES.has(s.section_type)) return <DesignerSection key={s.id} section={s} />;
          return null;
        })}

      <Footer />
    </div>
  );
};

export default GolfOutingPage;
